import { get as lodashGet } from "lodash";

const isObjEqual = (obj1 = {}, obj2 = {}, exclude = []) => {
	// check if both objects are not objects
	if (typeof obj1 !== "object" && typeof obj2 !== "object") {
		return obj1 === obj2;
	}
	// check if both objects are not null
	if (obj1 === null || obj2 === null) {
		return false;
	}
	// check if both objects have the same "number" of keys
	if (Object.keys(obj1).length !== Object.keys(obj2).length) {
		return false;
	}
	// check if both objects have the same keys
	for (const key in obj1) {
		if (exclude.includes(key)) continue;
		// eslint-disable-next-line no-prototype-builtins
		if (!obj2.hasOwnProperty(key)) {
			return false;
		}
	}
	// check if both objects have the same values
	for (const key in obj1) {
		if (exclude.includes(key)) continue;
		if (!isObjEqual(obj1[key], obj2[key])) {
			return false;
		}
	}
	return true;
};

const isSimilarObject = (previous, current, excludeKeys = ["_id"]) => {
	// Check if the previous and current are dates
	if (Date.parse(previous) && Date.parse(current)) {
		return Date.parse(previous) === Date.parse(current);
	}

	// Check if the previous and current are equal values and at least one is defined
	if (
		!previous ||
		!current ||
		(typeof previous !== "object" && typeof current !== "object") ||
		(!Object.keys(previous).length && !Object.keys(current).length)
	) {
		return JSON.stringify(previous) === JSON.stringify(current);
	}

	// Check if the previous and current are arrays
	if (Array.isArray(previous) && Array.isArray(current)) {
		if (previous.length !== current.length) return false;
		return !previous.some((previousObj) => !current.some((currentObj) => isSimilarObject(previousObj, currentObj, excludeKeys)));
	}

	// Remove exclusions keys from (both) previous and current
	for (const prop of excludeKeys) {
		delete previous[prop];
		delete current[prop];
	}

	// Ask for different keys on objects, sort keys and compare
	if (
		Object.keys(previous).length !== Object.keys(current).length ||
		JSON.stringify(Object.keys(previous).sort()) !== JSON.stringify(Object.keys(current).sort())
	) {
		return false;
	}

	// Loop over all keys in previous and do the recursive compare
	for (const key in previous) {
		if (!isSimilarObject(previous[key], current[key], excludeKeys)) {
			return false;
		}
	}

	return true;
};

const getValueOnProperty = (property = "", object = null) => {
	if (!property && !object) {
		return object;
	}
	return lodashGet(object, property, null);
};

const getAttributes = (content = null, excludes = []) => {
	if (Boolean(content)) {
		const reference = { ...content };
		return Object.keys(reference)?.reduce((accumulator, key) => {
			if (!excludes.includes(key)) {
				accumulator[key] = reference?.[key];
			}
			return accumulator;
		}, {});
	}
	return content;
};

const getContentType = (content) => {
	let type = (typeof content).toUpperCase();
	switch (type) {
		case "OBJECT": {
			const innerType = Object.prototype.toString.call(content).toUpperCase();
			switch (innerType) {
				case "[OBJECT OBJECT]":
					return "OBJECT";
				case "[OBJECT ARRAY]":
					return "ARRAY";
				case "[OBJECT NULL]":
					return "NULL";
				case "[OBJECT UNDEFINED]":
					return "UNDEFINED";
				case "[OBJECT DATE]":
					return "DATE";
				case "[OBJECT NUMBER]":
					return "NUMBER";
				case "[OBJECT BOOLEAN]":
					return "BOOLEAN";
				default:
					break;
			}
			break;
		}
		default:
			break;
	}
	return type;
};

const hasSameType = (contents = []) => {
	const typeToCompare = getContentType(contents?.[0]);
	return contents?.every((item) => typeToCompare === getContentType(item));
};

const hasSameProperties = (previous, current, excludeKeys = ["_id"], notEmpty = false) => {
	const isComparable = hasSameType([previous, current]);
	if (!isComparable) {
		return false;
	}

	const isArray = Array.isArray(current);
	if (isArray) {
		if (current.length !== previous.length) {
			return false;
		}
		if (notEmpty && Boolean(!current.length || !previous.length)) {
			return false;
		}
		return current?.every((element, key) => hasSameProperties(previous?.[key], element, excludeKeys, notEmpty));
	}

	if (notEmpty && Boolean(!current || !previous)) {
		return false;
	}
	const onType = getContentType(previous);
	switch (onType) {
		case "OBJECT": {
			const previousCompare = getAttributes(previous, excludeKeys);
			const currentCompare = getAttributes(current, excludeKeys);
			return Object.keys(previousCompare)?.every((section) =>
				hasSameProperties(previousCompare?.[section], currentCompare?.[section], excludeKeys, notEmpty)
			);
		}
		case "DATE": {
			return Date.parse(previous) === Date.parse(current);
		}
		default: {
			return Boolean(previous === current);
		}
	}
};

const hasKey = (object, keyToCheck) => {
	const keys = Object.entries(object || {});
	for (const [key] of keys) {
		if (key === keyToCheck) {
			return true;
		}
	}
	return false;
};

/**
 * Set unique elements from a list of objects.
 * @function setObjectsList
 * @description Function to produce a set of unique elements from a list of objects.
 * @param {Array} list - The list to filter.
 * @param {Array} keys - The list of attributes to filter by.
 * @returns {Array} - The Set list with the unique elements.
 */
const setObjectsList = (list = [], keys = []) => {
	if (!list?.length || !keys?.length) return list;

	const objects = new Map();

	const getObjectKey = (element, keys = []) => {
		return keys.reduce((acc, key) => {
			if (element?.[key]) {
				return `${acc}${element?.[key]}_`.trim();
			}
		}, "");
	};

	for (let i = 0; i < list.length; i++) {
		const key = getObjectKey(list[i], keys);
		if (!objects.get(key)) {
			objects.set(key, list[i]);
		}
	}

	return Array.from(objects.values());
};

export {
	hasSameType,
	hasSameProperties,
	isObjEqual,
	isSimilarObject,
	getAttributes,
	getContentType,
	getValueOnProperty,
	hasKey,
	setObjectsList,
};
